// ── Page-contextual chat suggestions ────────────────────────────────────────
//
// Starter prompts keyed by `pageContext.page`. Rendered above the composer in
// the empty state of a page-scoped chat (see chat.tsx) and as default chat
// starters on surfaces with no inline composer (e.g. the posture dashboard).
//
// Kept in its own module so lightweight surfaces can import the constant
// without pulling in the heavy `messages.tsx` chat-rendering tree.

export const PAGE_SUGGESTIONS: Record<string, string[]> = {
  // Onboarding welcome step — starters for a founder who just met Vera and
  // hasn't begun setup yet. Scoped to the welcome screen (its own page key)
  // so later wizard steps keep their per-question context instead.
  'screenata-onboarding-welcome': [
    'Which frameworks do I actually need?',
    'What will onboarding walk me through?',
    'How does Screenata get me audit-ready?',
  ],
  // Default starters for every other onboarding step. Generic enough to apply
  // to any question/form — Vera already has the specific step in pageContext.
  'screenata-onboarding': [
    'How do I fill in this step?',
    'What do you recommend for my company?',
    'Why does Screenata ask this?',
  ],
  // Short-label variants for the chat home hero, kept terse so all three
  // pills fit on a single line in the fixed-width (680px) welcome column.
  home: [
    'What should I work on next?',
    'Summarize my compliance gaps',
    'Which tests need attention?',
  ],
  dashboard: [
    'What should I work on next?',
    'Summarize my current compliance gaps',
    'Which controls need attention first?',
  ],
  // Security-questionnaire review page — a founder working a buyer's questionnaire.
  'questionnaire-review': [
    'What questions still need my input?',
    'Draft the remaining questions',
    "Summarize what Vera couldn't answer",
  ],
  readiness: [
    "What's the fastest way to improve my readiness score?",
    'Which controls need attention first?',
    'Summarize my current compliance gaps',
  ],
  controls: [
    'Which controls are failing?',
    'Explain what CC6.1 requires',
    'How do I satisfy the controls that are not yet covered?',
  ],
  controlDetail: [
    'Explain what this control requires',
    'What evidence do I need for this control?',
    'How do I satisfy this control?',
  ],
  'test-detail': [
    'What evidence do I need for this test?',
    'Help me collect evidence for this test',
    'Explain what this test is checking',
  ],
  risks: [
    'What are my highest-priority risks?',
    'Help me assess a new risk',
    'Suggest risk treatments for my critical items',
  ],
  policies: [
    'Which policies still need review?',
    'Help me improve my Access Control Policy',
    'What policies am I missing for SOC 2?',
  ],
  evidence: [
    'What evidence is expiring soon?',
    'Help me collect evidence for a specific control',
    'Which controls are missing evidence?',
  ],
  vendors: [
    'Which vendors need security reviews?',
    'Help me assess a new vendor',
    'What vendor risks should I be aware of?',
  ],
  assets: [
    'Which assets still need classification?',
    'Show me critical assets without an owner',
    'Help me prepare my asset inventory for audit',
  ],
  'asset-detail': [
    'What classification should this asset have?',
    'Which controls apply to this asset?',
    'What evidence do I need to collect for this asset?',
  ],
  documents: [
    'Which documents are missing or out of date?',
    'Help me organize my evidence library',
    'What documents will auditors ask for?',
  ],
  'document-detail': [
    'Summarize this document',
    'What claims or controls does this document support?',
    'Is anything in this document outdated or risky?',
  ],
  people: [
    'Who still owes security training?',
    'Which access reviews are overdue?',
    'Help me run an offboarding checklist',
  ],
}
